import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import { Button, Icon, message, YunxinBottom } from '@/components';
import RTCModel from '@/models/rtc';
import MeetingModel from '@/models/meeting';
import UserModel from '@/models/user';
import Style from './index.less';
import DeviceCheck from './component/DeviceCheck';
import LocalMp3 from '@/assets/test1.mp3';
import { useAudioWave } from '@/components/AudioWave';
import { elkLog } from '@/utils/elkReport';

interface CheckProps {
  RTCInstance?: RTCModel;
  meeting?: MeetingModel;
  user?: UserModel;
  onClose: () => void;
  mustCheck: boolean;
  joinMeeting: () => void;
  joinLoading: boolean;
}

const Check: React.FC<CheckProps> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
  };
})(
  observer((props) => {
    const { RTCInstance, meeting, onClose, mustCheck, joinMeeting, joinLoading } = props;
    const { formatMessage } = useIntl();
    const avatarWrapRef: any = React.useRef();
    const [currentAvatarId, setCurrentAvatarId] = React.useState<number>(0);

    const [isSpeakerChecking, setIsSpeakerChecking] = React.useState<boolean>(true);
    const [isMicChecking, setIsMicChecking] = React.useState<boolean>(false);
    const [isSpeakerChecked, setIsSpeakerChecked] = React.useState<boolean>(false);
    const [isMicChecked, setIsMicChecked] = React.useState<boolean>(false);
    const [isMicOk, setIsMicOk] = React.useState<boolean>(false);
    const [isSpeakerOk, setIsSpeakerOk] = React.useState<boolean>(false);
    const [micIsLoading, setMicIsLoading] = React.useState<boolean>(false);

    const audioRef = useRef<HTMLAudioElement>(null);

    const { waveValue, setSource, pauseStream, startStream, isRecording, maxVolume } =
      useAudioWave(null);

    const micSourceRef = useRef<MediaStream | null>(null);

    React.useEffect(() => {
      setCurrentAvatarId(meeting!.defaultAvatarId);
    }, [meeting?.defaultAvatarId]);

    React.useEffect(() => {
      if (
        avatarWrapRef &&
        avatarWrapRef.current &&
        meeting?.avatarMap &&
        currentAvatarId &&
        meeting?.avatarMap.has(currentAvatarId)
      ) {
        avatarWrapRef.current.style.backgroundImage = `url(${
          meeting?.avatarMap.get(currentAvatarId).cover['320']
        })`;
      }
    }, [currentAvatarId]);

    const title = useMemo(() => {
      if (isMicChecking) {
        if (isRecording) {
          return 'MICROPHONE_RECORDING_TIPS';
        } else {
          return 'MICROPHONE_BROADCASTING_TIPS';
        }
      }
      if (isSpeakerChecking) {
        return 'SPEAKER_CHECKER_TIPS';
      }
      return '';
    }, [isMicChecking, isSpeakerChecking, isRecording]);
    const options = isMicChecking
      ? RTCInstance?.deviceOptions.audioIn
      : isSpeakerChecking
      ? RTCInstance?.deviceOptions.audioOut
      : [];
    const selectLabel = useMemo(() => {
      if (isMicChecking) {
        return 'CHOOSE_MICROPHONE';
      }
      if (isSpeakerChecking) {
        return 'CHOOSE_SPEAKER';
      }
      return '';
    }, [isMicChecking, isSpeakerChecking]);
    const levelLabel = useMemo(() => {
      if (isMicChecking) {
        return 'INPUT_LEVEL';
      }
      if (isSpeakerChecking) {
        return 'OUTPUT_LEVEL';
      }
      return '';
    }, [isMicChecking, isSpeakerChecking]);
    const nextDeviceText = useMemo(() => {
      if (isMicChecking) {
        return 'NO_VOICE_NEXT_MICROPHONE';
      }
      if (isSpeakerChecking) {
        return 'NO_SOUND_NEXT_SPEAKER';
      }
      return '';
    }, [isMicChecking, isSpeakerChecking]);
    const device = isMicChecking
      ? RTCInstance?.audioInValue
      : isSpeakerChecking
      ? RTCInstance?.audioOutValue
      : '';

    const onDeviceChange = useCallback(
      async (value: string) => {
        if (isMicChecking) {
          if (!RTCInstance?.isOpenAudio) {
            await RTCInstance?.openMic(false, value);
          } else {
            await RTCInstance?.setAudioInValue.bind(RTCInstance)(value);
          }
        } else if (isSpeakerChecking) {
          await RTCInstance?.setAudioOutValue.bind(RTCInstance)(value);
          console.log(value);
        }
      },
      [isMicChecking, isSpeakerChecking, RTCInstance]
    );

    const confirmDevice = useCallback(async () => {
      if (isSpeakerChecking) {
        if (RTCInstance?.deviceOptions.audioIn && RTCInstance?.deviceOptions.audioIn.length > 0) {
          setMicIsLoading(true);
          if (!RTCInstance?.isOpenAudio) {
            await RTCInstance?.openMic(false, RTCInstance.deviceOptions.audioIn[0].value || '');
          } else {
            await RTCInstance?.setAudioInValue.bind(RTCInstance)(
              RTCInstance.deviceOptions.audioIn[0].value || ''
            );
          }
          if (!micSourceRef.current) {
            await navigator.mediaDevices
              .getUserMedia({ audio: true })
              .then((stream) => {
                micSourceRef.current = stream;
                setSource(stream);
              })
              .catch((err) => {
                console.log(err);
              });
          } else {
            setSource(micSourceRef.current);
          }
          setMicIsLoading(false);
        } else {
          setSource(null);
          message.warning({
            content: formatMessage({ id: 'NO_DEVICE' }),
          });
        }
        setIsMicChecking(true);
        setIsSpeakerChecking(false);
        setIsSpeakerOk(true);
        setIsSpeakerChecked(true);
        elkLog('检测扬声器有声音');
      } else if (isMicChecking) {
        setIsMicChecking(false);
        setIsMicOk(true);
        setIsMicChecked(true);
        micSourceRef.current && pauseStream(micSourceRef.current, true);
        elkLog('检测麦克风有声音');
        // RTCInstance?.setAudioOutValue.bind(RTCInstance)(value);
      }
    }, [
      isMicChecking,
      isSpeakerChecking,
      RTCInstance,
      micSourceRef.current,
      pauseStream,
      setSource,
    ]);

    const nextDevice = useCallback(() => {
      if (isMicChecking) {
        const micOptions = RTCInstance?.deviceOptions.audioIn;
        if (
          !micOptions ||
          !micOptions.length ||
          RTCInstance?.audioInValue === micOptions[micOptions?.length - 1].value
        ) {
          setIsMicChecked(true);
          setIsMicOk(false);
          micSourceRef.current && pauseStream(micSourceRef.current, true);
        } else {
          RTCInstance?.setAudioInValueNext();
        }
        elkLog('点击测试下一麦克风');
      } else if (isSpeakerChecking) {
        const speakerOptions = RTCInstance?.deviceOptions.audioOut;
        if (
          !speakerOptions ||
          !speakerOptions.length ||
          RTCInstance?.audioOutValue === speakerOptions[speakerOptions?.length - 1].value
        ) {
          setIsSpeakerChecked(true);
          setIsSpeakerOk(false);
          const audio = audioRef.current as HTMLAudioElement;
          audio.pause();
          setSource(null);
        } else {
          RTCInstance?.setAudioOutValueNext();
          elkLog('点击测试下一扬声器');
        }
      }
    }, [
      isMicChecking,
      isSpeakerChecking,
      RTCInstance,
      micSourceRef.current,
      pauseStream,
      setSource,
    ]);

    const AudioStart = useCallback(() => {
      const audio = audioRef.current as HTMLAudioElement;
      setSource(audio);
      if (audio) {
        audio.currentTime = 0;
        audio?.play();
      }
    }, [audioRef.current, setSource]);

    const reCheck = useCallback(async () => {
      elkLog('重新测试', {}, { isSpeakerOk, isMicOk });
      if (isMicChecked && !isMicOk) {
        if (RTCInstance?.isOpenAudio) {
          await RTCInstance?.setAudioInValue(RTCInstance.deviceOptions.audioIn[0].value || '');
        }
        micSourceRef.current && setSource(micSourceRef.current);
        micSourceRef.current && startStream(micSourceRef.current, true);
        setIsMicChecking(true);
        setIsMicChecked(false);
        setIsMicOk(false);
      } else if (isSpeakerChecked && !isSpeakerOk) {
        await RTCInstance?.setAudioOutValue(RTCInstance.deviceOptions.audioOut[0].value || '');
        setIsSpeakerChecking(true);
        setIsSpeakerChecked(false);
        setIsSpeakerOk(false);
        AudioStart();
        setIsMicChecking(false);
        setIsMicChecked(false);
        setIsMicOk(false);
      }
    }, [
      RTCInstance,
      AudioStart,
      micSourceRef.current,
      isMicChecked,
      isMicOk,
      startStream,
      isSpeakerChecked,
      isSpeakerOk,
      setSource,
    ]);

    function AudioVolumeChange(this: HTMLAudioElement, e: Event) {
      console.log(this.volume);
    }

    useEffect(() => {
      const audio = audioRef.current as HTMLAudioElement;
      if (isSpeakerChecking) {
        AudioStart();
        audio.addEventListener('ended', AudioStart);
        audio.addEventListener('volumechange', AudioVolumeChange);
      } else {
        audio.pause();
      }
    }, [isSpeakerChecking]);

    React.useEffect(() => {
      if (isMicChecking && !RTCInstance?.isOpenAudio) {
        RTCInstance?.openMic.bind(RTCInstance)(false);
      }
    }, [isMicChecking]);

    React.useEffect(() => {
      if (isWeb) {
        setSource(audioRef.current);
      }
    }, []);

    const bothError = useMemo(() => {
      return isMicChecked && !isMicOk && isSpeakerChecked && !isSpeakerOk;
    }, [isMicChecked, isMicOk, isSpeakerChecked, isSpeakerOk]);

    const micError = useMemo(() => {
      return isMicChecked && !isMicOk;
    }, [isMicChecked, isMicOk]);

    const hasError = useMemo(() => {
      return (isMicChecked && !isMicOk) || (isSpeakerChecked && !isSpeakerOk);
    }, [isMicChecked, isMicOk, isSpeakerChecked, isSpeakerOk]);

    const chosenMic = RTCInstance?.deviceOptions.audioIn.find(
      (item) => item.value === RTCInstance.audioInValue
    );
    const chosenSpeaker = RTCInstance?.deviceOptions.audioOut.find(
      (item) => item.value === RTCInstance.audioOutValue
    );
    const noDevice =
      (isSpeakerChecking &&
        (!!!RTCInstance?.deviceOptions.audioOut ||
          RTCInstance?.deviceOptions.audioOut.length === 0)) ||
      (isMicChecking &&
        (!!!RTCInstance?.deviceOptions.audioIn || RTCInstance?.deviceOptions.audioIn.length === 0));

    const handleCloseMICCheck = React.useCallback(() => {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          stream.getTracks().forEach((track) => track.stop());
        })
        .catch(() => {
          console.log('no audio device found');
        })
        .finally(() => {
          audioRef.current?.pause();
          micSourceRef.current && pauseStream(micSourceRef.current, true);
          onClose();
          elkLog('点击完成测试', {}, { isSpeakerOk, isMicOk });
        });
    }, [audioRef.current, micSourceRef.current, onClose, pauseStream]);

    useEffect(() => {
      RTCInstance?.setAudioOutValue(RTCInstance.deviceOptions.audioOut[0].value || '');
    }, []);

    return (
      <div className={Style['popo-meeting-check-wrap']}>
        <div className={Style['popo-meeting-check']}>
          <div className={Style.header}>
            <span>{formatMessage({ id: 'CHECK_DEVICES' })}</span>
            <span onClick={handleCloseMICCheck}>
              <Icon name="icon-close_nor" size={16} />
            </span>
            <audio src={LocalMp3} ref={audioRef} id="audio" />
          </div>
          {hasError || (isMicChecked && isSpeakerChecked) ? (
            <div className={Style.result}>
              <div className={Style.center}>
                <div className={Style['res-title']}>
                  {mustCheck ? (
                    hasError ? (
                      <div>{formatMessage({ id: 'DEVICE_NOT_WORK' })}</div>
                    ) : (
                      <div>{formatMessage({ id: 'SPEAKER_MIC_WORKED' })}</div>
                    )
                  ) : bothError || micError ? (
                    <div>{formatMessage({ id: 'SPEAKER_MIC_ERR' })}</div>
                  ) : isSpeakerChecked && !isSpeakerOk ? (
                    <div>{formatMessage({ id: 'SPEAKER_ERR' })}</div>
                  ) : (
                    <div>{formatMessage({ id: 'SPEAKER_MIC_WORKED' })}</div>
                  )}
                  {hasError && mustCheck && (
                    <div>{formatMessage({ id: 'CHANGE_AUDIO_RECHECK' })}</div>
                  )}
                </div>
                <div className={Style['res-content']}>
                  <div className={Style[`check-device`]}>
                    {isSpeakerChecked && <span>{formatMessage({ id: 'SPEAKER' })}</span>}
                    {isMicChecked && <span>{formatMessage({ id: 'MICROPHONE' })}</span>}
                  </div>
                  <div className={Style[`check-res`]}>
                    {isSpeakerChecked && (
                      <div className={Style[`check-${isSpeakerOk}`]}>
                        <span>
                          {chosenSpeaker ? chosenSpeaker.label : formatMessage({ id: 'NO_DATA' })}
                        </span>
                        {isSpeakerOk ? (
                          <Icon name="icon-chosen_nor" size={14} />
                        ) : (
                          <Icon name="icon-close_nor" size={14} />
                        )}
                      </div>
                    )}
                    {isMicChecked && (
                      <div className={Style[`check-${isMicOk}`]}>
                        <span>
                          {chosenMic ? chosenMic.label : formatMessage({ id: 'NO_DATA' })}
                        </span>
                        {isMicOk ? (
                          <Icon name="icon-chosen_nor" size={14} />
                        ) : (
                          <Icon name="icon-close_nor" size={14} />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className={Style['res-footer']}>
                  {(hasError && mustCheck) || (!isSpeakerOk && !mustCheck) ? (
                    <Button
                      onClick={handleCloseMICCheck}
                      type="primary"
                      className={Style['end-btn']}
                    >
                      {formatMessage({ id: 'END_CHECK' })}
                    </Button>
                  ) : mustCheck ? (
                    <Button
                      onClick={joinMeeting}
                      type="primary"
                      loading={joinLoading}
                      className={Style['join-btn']}
                    >
                      {formatMessage({ id: 'JOIN_MEETING' })}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleCloseMICCheck}
                      type="primary"
                      className={Style['end-btn']}
                    >
                      {formatMessage({ id: 'COMPLETE' })}
                    </Button>
                  )}
                  {hasError && (
                    <Button onClick={reCheck} type="primary" className={Style['recheck-btn']}>
                      {formatMessage({ id: 'RECHECK' })}
                    </Button>
                  )}
                </div>
              </div>
              <YunxinBottom />
            </div>
          ) : (
            <div className={Style.content}>
              <div className={Style.left}>
                <div className={Style['device-name']}>
                  <span>{formatMessage({ id: 'SPEAKER' })}</span>
                  <span>{formatMessage({ id: 'MICROPHONE' })}</span>
                </div>
                <div className={Style['device-icon']}>
                  {isSpeakerChecking && (
                    <Icon name="icon-zhengzaijianceyangshengqi" mode="symbol" size={40} />
                  )}
                  {isSpeakerOk && <Icon name="icon-gongzuozhengchang" mode="symbol" size={40} />}
                  {!isSpeakerChecked && !isSpeakerChecking && (
                    <Icon name="icon-daijianceyangshengqi" mode="symbol" size={40} />
                  )}
                  {isMicChecking && (
                    <Icon name="icon-zhengzaijiancemaikefengxin1" mode="symbol" size={40} />
                  )}
                  {isMicOk && <Icon name="icon-gongzuozhengchang" mode="symbol" size={40} />}
                  {!isMicChecked && !isMicChecking && (
                    <Icon name="icon-jiancemaikefeng2" mode="symbol" size={40} />
                  )}
                </div>
              </div>
              <div className={Style.right}>
                <DeviceCheck
                  title={title}
                  options={options || []}
                  selectLabel={selectLabel}
                  levelLabel={levelLabel}
                  onChange={onDeviceChange}
                  device={device!}
                  deviceVolume={waveValue}
                  maxVolume={maxVolume}
                />
                <div className={Style.button}>
                  <Button
                    onClick={() => confirmDevice()}
                    type="primary"
                    className={Style['confirm-btn']}
                    disabled={noDevice}
                    loading={micIsLoading}
                  >
                    {formatMessage({ id: 'YES' })}
                  </Button>
                  <Button onClick={nextDevice} type="primary" className={Style['no-device-btn']}>
                    {formatMessage({ id: nextDeviceText })}
                  </Button>
                </div>
              </div>
              <YunxinBottom />
            </div>
          )}
        </div>
      </div>
    );
  })
);
export default Check;
